import { useRef, useState } from 'react';
import { Button, Overlay, Popover } from 'react-bootstrap';
import { DayPicker, useNavigation, type CaptionProps } from 'react-day-picker';
import { format, type Locale } from 'date-fns';
import { useTranslation } from 'react-i18next';
import uk from 'date-fns/locale/uk';
import pl from 'date-fns/locale/pl';
import enGB from 'date-fns/locale/en-GB';
import SolarIcon from 'components/icons/SolarIcon';
import { getLocale } from 'utils/helpers/functions/getLocale';
import 'react-day-picker/dist/style.css';

export type TaskCalendarDatePickerProps = {
    title: string;
    currentDate: Date;
    onGoToDate: (date: Date) => void;
};

const resolveDateLocale = (language: string) => {
    const code = getLocale(language);
    if (code.startsWith('pl')) return pl;
    if (code.startsWith('uk')) return uk;
    return enGB;
};

type DatePickerCaptionProps = CaptionProps & {
    locale: Locale;
    prevLabel: string;
    nextLabel: string;
};

const DatePickerCaption = ({
    displayMonth,
    locale,
    prevLabel,
    nextLabel,
}: DatePickerCaptionProps) => {
    const { goToMonth, nextMonth, previousMonth } = useNavigation();

    return (
        <div className="d-flex align-items-center gap-1 mb-2">
            <Button
                size="sm"
                variant="light-secondary"
                className="toolbar-icon-btn"
                disabled={!previousMonth}
                onClick={() => previousMonth && goToMonth(previousMonth)}
                aria-label={prevLabel}
            >
                <SolarIcon name="chevron-left" aria-hidden="true" />
            </Button>
            <span className="flex-grow-1 text-center text-body fw-medium text-capitalize">
                {format(displayMonth, 'LLLL yyyy', { locale })}
            </span>
            <Button
                size="sm"
                variant="light-secondary"
                className="toolbar-icon-btn"
                disabled={!nextMonth}
                onClick={() => nextMonth && goToMonth(nextMonth)}
                aria-label={nextLabel}
            >
                <SolarIcon name="chevron-right" aria-hidden="true" />
            </Button>
        </div>
    );
};

const TaskCalendarDatePicker = ({
    title,
    currentDate,
    onGoToDate,
}: TaskCalendarDatePickerProps) => {
    const { t, i18n } = useTranslation(['Tasks']);
    const triggerRef = useRef<HTMLSpanElement>(null);
    const [show, setShow] = useState(false);
    const locale = resolveDateLocale(i18n.language);

    return (
        <>
            <span
                ref={triggerRef}
                className="task-calendar__date-title d-inline-flex align-items-center gap-1 text-body fw-medium"
                onClick={() => setShow((open) => !open)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setShow((open) => !open);
                    }
                }}
                role="button"
                tabIndex={0}
                title={t('calendar.pickDate')}
            >
                {title}
                <SolarIcon name="chevron-down" aria-hidden="true" />
            </span>
            <Overlay
                target={triggerRef}
                show={show}
                placement="bottom-start"
                rootClose
                onHide={() => setShow(false)}
            >
                <Popover id="task-calendar-date-picker" className="task-calendar__datepicker-popover">
                    <Popover.Body className="p-2">
                        <DayPicker
                            mode="single"
                            selected={currentDate}
                            defaultMonth={currentDate}
                            locale={locale}
                            weekStartsOn={1}
                            components={{
                                Caption: (props) => (
                                    <DatePickerCaption
                                        {...props}
                                        locale={locale}
                                        prevLabel={t('calendar.prev')}
                                        nextLabel={t('calendar.next')}
                                    />
                                ),
                            }}
                            onSelect={(date) => {
                                if (!date) return;
                                onGoToDate(date);
                                setShow(false);
                            }}
                        />
                    </Popover.Body>
                </Popover>
            </Overlay>
        </>
    );
};

export default TaskCalendarDatePicker;
